import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo, useState } from 'react';

import {
  AddOptionRow,
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxStatus,
  ComboboxValue,
  useAddableCombobox,
  type ComboboxProps,
} from '@/components/Form/pickers';

export type FreeFormSelectProps = Omit<
  ComboboxProps<string, true>,
  | 'items'
  | 'multiple'
  | 'value'
  | 'onValueChange'
  | 'itemToStringValue'
  | 'itemToStringLabel'
  | 'disabled'
> & {
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  value?: string[];
};

const FreeFormSelect = ({
  value,
  className,
  disabled = false,
  size = 'large',
  placeholder = t`Type values and press Enter to add`,
  onInputValueChange,
  'aria-invalid': ariaInvalid,
  ...props
}: FreeFormSelectProps) => {
  const [inputSearch, setInputSearch] = useState('');
  const { isAddOption, isAddOptionSelected, itemsWithAddOption } = useAddableCombobox({
    inputSearch,
    value,
    items: [],
  });

  const items = useMemo(() => {
    const selected = value ?? [];
    const seen = new Set<string>();
    return [...selected, ...itemsWithAddOption].filter((item) => {
      if (item.length === 0 || seen.has(item)) return false;
      seen.add(item);
      return true;
    });
  }, [value, itemsWithAddOption]);

  return (
    <Combobox
      {...props}
      data-testid="free-form-select"
      items={items}
      disabled={disabled}
      size={size}
      value={value}
      multiple
      onInputValueChange={(nextSearch, eventDetails) => {
        setInputSearch(nextSearch);
        onInputValueChange?.(nextSearch, eventDetails);
      }}
    >
      <ComboboxChips className={className} aria-invalid={ariaInvalid}>
        <ComboboxValue>
          {(value ?? []).map((item) => (
            <ComboboxChip key={item}>{item}</ComboboxChip>
          ))}
        </ComboboxValue>
        <ComboboxChipsInput placeholder={placeholder} />
      </ComboboxChips>
      <ComboboxContent>
        <ComboboxStatus />
        <ComboboxEmpty>
          <Trans>Type values and press Enter to add</Trans>
        </ComboboxEmpty>
        <ComboboxList>
          {(item: string) => (
            <ComboboxItem key={item} value={item}>
              {isAddOption(item) ? (
                <AddOptionRow label={item} isSelected={isAddOptionSelected} />
              ) : (
                item
              )}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};

export default FreeFormSelect;
